import React from 'react';
import { X, Calendar, DollarSign, MapPin, Image, Users, FileText, Eye, Edit, Trash2 } from 'lucide-react';

const SubmissionViewModal = ({ isOpen, onClose, submission, onEdit, onDelete }) => {
  if (!isOpen || !submission) return null;

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-red-100 text-red-800',
      archived: 'bg-gray-100 text-gray-600'
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusStyles[status] || statusStyles.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Draft'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{submission.title}</h2>
            <p className="text-gray-600 mt-1">Submission Details</p>
          </div>
          <div className="flex items-center space-x-3">
            {onEdit && (
              <button
                onClick={() => onEdit(submission)}
                className="text-indigo-600 hover:text-indigo-900 p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                title="Edit Submission"
              >
                <Edit className="w-5 h-5" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(submission.submission_id)}
                className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Submission"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 text-purple-600 mr-2" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <p className="text-gray-900">{submission.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                <p className="text-gray-900">{submission.theme}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Featured</label>
                <div className="mt-1">{submission.featured ? 'Yes' : 'No'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submission ID</label>
                <p className="text-gray-900 font-mono text-sm">{submission.submission_id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <p className="text-gray-900 font-semibold">{formatCurrency(submission.submission_price)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Allowed</label>
                <p className="text-gray-900">{submission.max_allowed || 'Unlimited'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <p className="text-gray-900">{formatDate(submission.submission_deadline)}</p>
              </div>
                             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Submission ID</label>
                 <p className="text-gray-900">{submission.submission_id}</p>
               </div>
            </div>
          </div>

          {/* Gallery and Collection */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 text-blue-600 mr-2" />
              Gallery & Collection
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gallery</label>
                <p className="text-gray-900">Gallery ID: {submission.gallery_id || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection</label>
                <p className="text-gray-900">Collection ID: {submission.collection_id || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Callout Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="w-5 h-5 text-green-600 mr-2" />
              Callout Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Callout Headline</label>
                <p className="text-gray-900">{submission.callout_headline || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
                <p className="text-gray-900">{submission.cover_image || 'No cover image'}</p>
              </div>
            </div>
          </div>

          {/* Guidelines and Requirements */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 text-purple-600 mr-2" />
              Submission Guidelines
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guidelines</label>
                <p className="text-gray-900 whitespace-pre-wrap">{submission.submission_guidelines || 'No guidelines provided'}</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Artistic Guidelines</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{submission.artistic_guidelines || 'No artistic guidelines specified'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Promotion Guidelines</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{submission.promotion_guidelines || 'No promotion guidelines specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 text-orange-600 mr-2" />
              Important Dates
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Important Dates</label>
              <p className="text-gray-900 whitespace-pre-wrap">{submission.important_dates || 'No important dates provided'}</p>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="w-5 h-5 text-blue-600 mr-2" />
              Additional Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
                {submission.cover_image ? (
                  <div className="mt-2">
                    <img
                      src={submission.cover_image}
                      alt="Cover"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <p className="text-gray-500 text-sm mt-1 hidden">Image failed to load</p>
                    <a
                      href={submission.cover_image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View Full Image
                    </a>
                  </div>
                ) : (
                  <p className="text-gray-500">No cover image</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Product ID</label>
                <p className="text-gray-900 font-mono text-sm">{submission.stripe_product_id || 'Not configured'}</p>
              </div>
            </div>
          </div>



          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            {onEdit && (
              <button
                onClick={() => onEdit(submission)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Submission</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionViewModal;
